import React from "react";
import { CircleCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const capabilities = [{
  title: "PORTABLE DRILLING EQUIPMENT",
  description: "All-terrain drilling capability for difficult access sites, ensuring precise soil sampling regardless of ground conditions.",
  image: "https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/dji_fly_20220831_182030_650_1662153319230_photo_optimized-scaled-1.jpg?v=32t7zrdk7bb",
  category: "Drilling"
}, {
  title: "SUPPORTIVE EXCAVATION",
  description: "QST operates compact to heavy civil excavators (JD 35G–350G) supporting test pits, infiltration prep, and site investigations.",
  image: "https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/IMG_9010-scaled.jpg?v=6u2q5hhzc8r",
  category: "EXCAVTION",
  imageClassName: "absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10 !bg-[url(https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/IMG_9010-scaled.jpg)] absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10 !bg-[url(https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/IMG_9010-scaled.jpg)]"
}, {
  title: "INFILTRATION TESTING\xA0",
  description: "Infiltration and percolation testing with controlled water delivery and field-installed casings for reliable subsurface evaluation.",
  image: "https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/filled-ring-infiltrometer.webp?v=7umee0bh9pw",
  category: "Field Testing",
  imageClassName: "absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10 !bg-[url(https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/images.jpeg)] absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10 !bg-[url(https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/IMG_9010-scaled.jpg)] absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10 !bg-[url(https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/IMG_9010-scaled.jpg)]"
}, {
  title: "\xA0SUBSURFACE PROBING",
  description: "Interior and exterior probing to verify foundation, slab, and subsurface conditions for renovation and design support.",
  image: "https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/foundation-slab-structural.png?v=x4t1u4qrubs",
  category: "PROBING",
  imageClassName: "absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10 !bg-[url(https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/foundation-slab-structural.png)]"
}, {
  title: "SOIL BORINGS & SAMPLING",
  description: "Subsurface borings and sampling coordinated for geotechnical evaluation and design support.",
  image: "https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/Screenshot-2026-02-19-231837.png?v=6jcqh0ahjzy",
  category: "SAMPLING",
  imageClassName: "absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10 !bg-[url(https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/split-spoon-sampler.jpg)] absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10 !bg-[url(https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/Screenshot-2026-02-19-231837.png)] absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10 !bg-[url(https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/Screenshot-2026-02-19-231837.png)] absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10 !bg-[url(https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/Screenshot-2026-02-19-231837.png)]"
}, {
  title: "SOIL ANALYSIS & ENGINEERING",
  description: "Engineer-reviewed soil classification and infiltration data supporting septic design, stormwater review, and site development.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1550713/img/equipment_geophysical.png",
  category: "Investigation"
}];
export default function EquipmentFieldCapabilities() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="equipment-field-capabilities" ref={ref} className={`dark bg-background text-foreground py-24 px-4 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-primary blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-primary blur-[100px]" />
      </div>

      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-wider mb-6 text-foreground">
            Equipment & Field Capabilities
          </h2>
          <p className="text-lg text-muted-foreground">We maintain a fully owned and operated fleet of specialized geotechnical equipment, ensuring rapid mobilization and quality control on every project.&nbsp;</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {capabilities.map((item, i) => <div key={i} data-index={i} className="h-full">
              <Card className="h-full bg-card/50 border-border/20 backdrop-blur-sm hover:border-primary/50 hover:shadow-[0_0_30px_-10px_var(--color-primary)] transition-all duration-300 group overflow-hidden flex flex-col">
                <div className="relative h-56 w-full overflow-hidden">
                  
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 transform group-hover:scale-105" />
                  <div className="absolute top-4 right-4 z-20">
                    <Badge variant="default" className="bg-primary text-primary-foreground border-none shadow-lg font-bold tracking-wide uppercase text-[10px]">
                      Owned & Operated
                    </Badge>
                  </div>
                  <div className="absolute bottom-4 left-6 z-20">
                    <span className="text-xs font-bold uppercase tracking-widest mb-1 block !opacity-[0.5] !bg-[hsl(217,33%,17%)] !text-[hsl(0,0%,100%)]">
                      {item.category}
                    </span>
                  </div>
                </div>

                <CardHeader className="pb-2 pt-4">
                  <CardTitle className="text-xl font-bold uppercase tracking-tight text-foreground group-hover:text-primary transition-colors duration-300">
                    {item.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-grow">
                  <CardDescription className="text-muted-foreground text-base leading-relaxed">
                    {item.description}
                  </CardDescription>
                  
                  <div className="mt-6 pt-4 border-t border-border/10 flex items-center text-sm text-muted-foreground group-hover:text-foreground transition-colors duration-300">
                    <CircleCheck className="w-4 h-4 mr-2 text-primary" />
                    <span>Available for immediate deployment</span>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}